// src/components/ResponsiveSidebar.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  IconButton,
  List,
  Divider,
  Collapse,
  Toolbar,
  useTheme,
  useMediaQuery,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  AccountCircle as AccountCircleIcon,
  Person as PersonIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  WarningAmber as WarningAmberIcon,
  School as SchoolIcon,
  Warning as WarningIcon,
  LocalHospital as LocalHospitalIcon,
  Chat as ChatIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchBatches } from "../store/AdmissionInformationSlice.js";
import { fetchAdmissionPeriod } from "../store/admissionPeriodSlice.js";
import { setWhoIsTheUser } from "../store/WhoIsTheUserSlice.js";

const drawerWidthOpen = 240;
const drawerWidthClosed = 60;

const StyledListItem = styled(ListItem)(({ theme }) => ({
  "&:hover": {
    backgroundColor: "#4caf50",
    color: theme.palette.common.white,
    "& .MuiListItemIcon-root": {
      color: theme.palette.common.white,
    },
    "& .MuiListItemText-primary": {
      fontWeight: 600,
    },
    borderLeft: `3px solid ${theme.palette.primary.main}`,
  },
  padding: theme.spacing(1.5, 2),
  transition: "all 0.3s ease",
  cursor: "pointer",
   borderLeft: `3px solid #2e7d32`,
}));

const StyledNestedListItem = styled(StyledListItem)(({ theme }) => ({
  paddingLeft: theme.spacing(4),
  "& .MuiListItemText-primary": {
    color: "#c8e6c9",
  },
}));

export default function ResponsiveSidebar({ children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(true);

  // collapse states
  const [openStudentProfile, setOpenStudentProfile] = useState(false);
  const [openStudentDiscipline, setOpenStudentDiscipline] = useState(false);
  const [openAcademics, setOpenAcademics] = useState(false);
  const [openAttendance, setOpenAttendance] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((s) => s.auth);
  const { validatedBatchesForExistingParent, validatedBatchesForStaffs, error } =
    useSelector((s) => ({
      validatedBatchesForExistingParent: s.admissionInformation.batches?.validatedBatchesForExistingParent || [],
      validatedBatchesForStaffs: s.admissionInformation.batches?.validatedBatchesForStaffs || [],
      error: s.admissionInformation.error
    }));
  const { isStaff, isParent, isStudent } = useSelector((s) => s.whoIsTheUser || {});

  // set whoIsTheUser from localStorage
  useEffect(() => {
    if (isAuthenticated) {
      const stored = localStorage.getItem("userInfo");
      const roles = stored ? JSON.parse(stored).roles : [];
      dispatch(setWhoIsTheUser({ isAuthenticated, roles }));
    }
  }, [dispatch, isAuthenticated]);

  // fetch admission & batches
  useEffect(() => {
    dispatch(fetchAdmissionPeriod());
    dispatch(fetchBatches());
  }, [dispatch]);

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const sidebarWidth = isMobile
    ? drawerWidthOpen
    : drawerOpen
    ? drawerWidthOpen
    : drawerWidthClosed;

  const drawerContent = (
    <Box sx={{ bgcolor: "#2e7d32", height: "100%" }}>
      {/* Burger toggle */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: drawerOpen ? "flex-end" : "center",
          p: 1,
        }}
      >
        <IconButton
          onClick={() => setDrawerOpen((o) => !o)}
          sx={{ color: "#fff" }}
        >
          <MenuIcon />
        </IconButton>
      </Box>
      <Divider />

      <List disablePadding>
        {/* Dashboard */}
        {(isStudent || isParent) && (
          <StyledListItem onClick={() => handleNavigation("/dashboard")}>
            <ListItemIcon>
              <DashboardIcon htmlColor="#fff" />
            </ListItemIcon>
            {drawerOpen && (
              <ListItemText
                primary="Dashboard"
                primaryTypographyProps={{ color: "#fff", fontWeight: 500 }}
              />
            )}
          </StyledListItem>
        )}
        <Divider />

        {/* Student Profile */}
        {(isStudent || isParent) && (
          <>
            <StyledListItem
              onClick={() => setOpenStudentProfile((o) => !o)}
            >
              <ListItemIcon>
                <AccountCircleIcon htmlColor="#fff" />
              </ListItemIcon>
              {drawerOpen && (
                <ListItemText
                  primary="Student Profile"
                  primaryTypographyProps={{ color: "#fff", fontWeight: 500 }}
                />
              )}
            </StyledListItem>
            <Collapse in={openStudentProfile} timeout="auto" unmountOnExit>
              <List disablePadding>
                <StyledNestedListItem
                  onClick={() => handleNavigation("/personal-basic-info")}
                >
                  <ListItemIcon>
                    <PersonIcon htmlColor="#A8E6CE" />
                  </ListItemIcon>
                  {drawerOpen && <ListItemText primary="Personal Info" />}
                </StyledNestedListItem>
                <Divider />
                <StyledNestedListItem
                  onClick={() => handleNavigation("/student-health-info")}
                >
                  <ListItemIcon>
                    <LocalHospitalIcon htmlColor="#A8E6CE" />
                  </ListItemIcon>
                  {drawerOpen && <ListItemText primary="Health Info" />}
                </StyledNestedListItem>
                <Divider />
                <StyledNestedListItem
                  onClick={() => handleNavigation("/health-issue-edit")}
                >
                  <ListItemIcon>
                    <WarningAmberIcon htmlColor="#A8E6CE" />
                  </ListItemIcon>
                  {drawerOpen && <ListItemText primary="Health Issues" />}
                </StyledNestedListItem>
                <Divider />
                <StyledNestedListItem
                  onClick={() => handleNavigation("/student-academic-history")}
                >
                  <ListItemIcon>
                    <SchoolIcon htmlColor="#A8E6CE" />
                  </ListItemIcon>
                  {drawerOpen && <ListItemText primary="Academic History" />}
                </StyledNestedListItem>
              </List>
            </Collapse>
          </>
        )}
        <Divider />

        {/* Apply Now */}
        {isParent && !error && validatedBatchesForExistingParent.length > 0 && (
          <StyledListItem
            onClick={() =>
              handleNavigation("/application-enquiry-existingparent")
            }
          >
            <ListItemIcon>
              <AssignmentTurnedInIcon htmlColor="#fff" />
            </ListItemIcon>
            {drawerOpen && (
              <ListItemText
                primary="Apply Now (Parent)"
                primaryTypographyProps={{ color: "#fff", fontWeight: 500 }}
              />
            )}
          </StyledListItem>
        )}
        <Divider />
        {isStaff && !error && validatedBatchesForStaffs.length > 0 && (
          <StyledListItem
            onClick={() =>
              handleNavigation("/application-enquiry-existingparent")
            }
          >
            <ListItemIcon>
              <AssignmentTurnedInIcon htmlColor="#fff" />
            </ListItemIcon>
            {drawerOpen && (
              <ListItemText
                primary="Apply Now (Staff)"
                primaryTypographyProps={{ color: "#fff", fontWeight: 500 }}
              />
            )}
          </StyledListItem>
        )}
        <Divider />

        {/* Payment */}
        {(isStudent || isParent) && (
          <StyledListItem onClick={() => handleNavigation("/createPayment")}>
            <ListItemIcon>
              <DashboardIcon htmlColor="#fff" />
            </ListItemIcon>
            {drawerOpen && (
              <ListItemText
                primary="Payment"
                primaryTypographyProps={{ color: "#fff", fontWeight: 500 }}
              />
            )}
          </StyledListItem>
        )}
        <Divider />

        {/* Academics */}
        {(isStudent || isParent) && (
          <>
            <StyledListItem onClick={() => setOpenAcademics((o) => !o)}>
              <ListItemIcon>
                <WarningIcon htmlColor="#fff" />
              </ListItemIcon>
              {drawerOpen && (
                <ListItemText
                  primary="Academics"
                  primaryTypographyProps={{ color: "#fff", fontWeight: 500 }}
                />
              )}
            </StyledListItem>
            <Collapse in={openAcademics} timeout="auto" unmountOnExit>
              <List disablePadding>
                {/* <StyledNestedListItem
                  onClick={() => handleNavigation("/assessments")}
                >
                  <ListItemIcon>
                    <PersonIcon htmlColor="#A8E6CE" />
                  </ListItemIcon>
                  {drawerOpen && <ListItemText primary="Subject Assessments" />}
                </StyledNestedListItem> */}
                <Divider />
                <StyledNestedListItem
                  onClick={() => handleNavigation("/mark-list")}
                >
                  <ListItemIcon>
                    <LocalHospitalIcon htmlColor="#A8E6CE" />
                  </ListItemIcon>
                  {drawerOpen && <ListItemText primary="Mark List" />}
                </StyledNestedListItem>
                <Divider />
                <StyledNestedListItem
                  onClick={() => handleNavigation("/student-transcript")}
                >
                  <ListItemIcon>
                    <LocalHospitalIcon htmlColor="#A8E6CE" />
                  </ListItemIcon>
                  {drawerOpen && <ListItemText primary="Transcript" />}
                </StyledNestedListItem>
              </List>
            </Collapse>
          </>
        )}
        <Divider />

        {/* Attendance */}
        {(isStudent || isParent) && (
          <>
            {/* <StyledListItem onClick={() => setOpenAttendance((o) => !o)}>
              <ListItemIcon>
                <WarningIcon htmlColor="#fff" />
              </ListItemIcon>
              {drawerOpen && (
                <ListItemText
                  primary="Attendance"
                  primaryTypographyProps={{ color: "#fff", fontWeight: 500 }}
                />
              )}
            </StyledListItem> */}
            {/* <Collapse in={openAttendance} timeout="auto" unmountOnExit>
              <List disablePadding>
                <StyledNestedListItem
                  onClick={() => handleNavigation("/subject-attendance")}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <PersonIcon htmlColor="#A8E6CE" />
                  </ListItemIcon>
                  {drawerOpen && <ListItemText primary="Subject Attendance" />}
                </StyledNestedListItem>
                <Divider />
                <StyledNestedListItem
                  onClick={() => handleNavigation("/home-room-attendance")}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <LocalHospitalIcon htmlColor="#A8E6CE" />
                  </ListItemIcon>
                  {drawerOpen && <ListItemText primary="Homeroom Attendance" />}
                </StyledNestedListItem>
              </List>
            </Collapse> */}
          </>
        )}
        <Divider />

        {/* Disciplinary */}
        {(isStudent || isParent) && (
          <>
            <StyledListItem
              onClick={() => setOpenStudentDiscipline((o) => !o)}
            >
              <ListItemIcon>
                <WarningIcon htmlColor="#fff" />
              </ListItemIcon>
              {drawerOpen && (
                <ListItemText
                  primary="Disciplinary Issues"
                  primaryTypographyProps={{ color: "#fff", fontWeight: 500 }}
                />
              )}
            </StyledListItem>
            <Collapse in={openStudentDiscipline} timeout="auto" unmountOnExit>
              <List disablePadding>
                <StyledNestedListItem
                  onClick={() => handleNavigation("/student-desciplinary-records")}
                >
                  <ListItemIcon>
                    <PersonIcon htmlColor="#A8E6CE" />
                  </ListItemIcon>
                  {drawerOpen && <ListItemText primary="Student Disciplinary" />}
                </StyledNestedListItem>
                <Divider />
                <StyledNestedListItem
                  onClick={() => handleNavigation("/active-desciplinary-issues")}
                >
                  <ListItemIcon>
                    <LocalHospitalIcon htmlColor="#A8E6CE" />
                  </ListItemIcon>
                  {drawerOpen && <ListItemText primary="Active Disciplinary Issues" />}
                </StyledNestedListItem>
              </List>
            </Collapse>
          </>
        )}
        <Divider />

        {/* Communication */}
        {(isStudent || isParent) && (
          <StyledListItem onClick={() => handleNavigation("/communications")}>
            <ListItemIcon>
              <ChatIcon htmlColor="#fff" />
            </ListItemIcon>
            {drawerOpen && (
              <ListItemText
                primary="Communication"
                primaryTypographyProps={{ color: "#fff", fontWeight: 500 }}
              />
            )}
          </StyledListItem>
        )}
        <Divider />

        {/* Academic Calendar */}
        {(isStudent || isParent) && (
          <StyledListItem onClick={() => handleNavigation("/academic-calendar")}>
            <ListItemIcon>
              <DashboardIcon htmlColor="#fff" />
            </ListItemIcon>
            {drawerOpen && (
              <ListItemText
                primary="Academic Calendar"
                primaryTypographyProps={{ color: "#fff", fontWeight: 500 }}
              />
            )}
          </StyledListItem>
        )}
        <Divider />

        {/* Scheduling */}
        {/* {(isStudent || isParent) && (
          <StyledListItem onClick={() => handleNavigation("/schedule-data")}>
            <ListItemIcon>
              <DashboardIcon htmlColor="#fff" />
            </ListItemIcon>
            {drawerOpen && (
              <ListItemText
                primary="Scheduling"
                primaryTypographyProps={{ color: "#fff", fontWeight: 500 }}
              />
            )}
          </StyledListItem>
        )} */}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: sidebarWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: sidebarWidth,
            boxSizing: "border-box",
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${sidebarWidth}px)` },
          transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        }}
      >
        {/* ensure any Toolbar offset if needed */}
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
}
